import React, { useCallback, useContext, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import GlobalContext from "contexts/GlobalContext";
import { useTranslator } from "@sia/style-guide";
import { initializeCalculationFilters } from "./stateInitializers";
import { updateCalculationPageState } from "./stateUpdaters";
import CalculationPage from "./CalculationPage";
import { generateMiddleWare } from "pages/Evaluations/tools";
import useEvaluationMasterKeyAccess from "pages/Evaluations/useEvaluationMasterKeyAccess";
import { useEvaluationRequestScope } from "pages/Evaluations/evaluationRequestScope";

const emptyProfitMarginInputs = () => ({ pctOfGrossSalary: null, pctOfSelfCosts: null, lastChanged: null });

export default function CalculationsRoute() {
  const { user } = useContext(GlobalContext);
  const translate = useTranslator();
  const { i18n } = useTranslation();
  const history = useHistory();
  const { languageKey } = useParams();
  const access = useEvaluationMasterKeyAccess(user);
  const dataScopeKey = `${access.dataScopeKey}:${languageKey}`;
  const [calculationFilters, setCalculationFilters] = useState(initializeCalculationFilters(translate));
  const [calculationData, setCalculationData] = useState([]);
  const [profitMarginInputs, setProfitMarginInputs] = useState(emptyProfitMarginInputs);

  const resetCalculationState = useCallback(() => {
    setCalculationData([]);
    setProfitMarginInputs(emptyProfitMarginInputs());
    setCalculationFilters(initializeCalculationFilters(translate));
  }, [translate]);
  const { ready: isDataScopeReady, beginRequest } = useEvaluationRequestScope(dataScopeKey, resetCalculationState);

  useEffect(() => {
    if (!isDataScopeReady || !calculationFilters.newest || !user || !access.canFetch) return;
    const request = beginRequest("calculations");
    void updateCalculationPageState(
      access.isCompanyMode,
      languageKey,
      calculationFilters,
      request.guard(setCalculationFilters),
      request.guard(setCalculationData),
      profitMarginInputs,
      request.guard(setProfitMarginInputs),
      translate
    ).catch((error) => {
      if (request.isCurrent() && error?.code !== "CRYPTO_SESSION_CHANGED") console.error("Failed to load Calculation Evaluation data", error);
    });
  }, [access.canFetch, access.isCompanyMode, beginRequest, calculationFilters, isDataScopeReady, languageKey, profitMarginInputs, translate, user]);

  const onProfitMarginChange = (field, next) => {
    setProfitMarginInputs((previous) => ({ ...previous, [field]: next, lastChanged: field }));
    setCalculationFilters((previous) => ({ ...previous, newest: true }));
  };

  const middleware = generateMiddleWare(i18n, languageKey, user, translate, null, null, history, access, { withHeader: false });
  return middleware(
    <CalculationPage
      filters={calculationFilters.filters}
      setFilters={setCalculationFilters}
      data={calculationData}
      translate={translate}
      profitMarginInputs={profitMarginInputs}
      onProfitMarginChange={onProfitMarginChange}
    />
  );
}
